import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'

import { Subtitle1 } from '@src/elements/typography/typography'
import { CircumferencesForm } from '@src/routes/members/components/body-measurements/form'
import { HistoryTable } from '@src/routes/members/components/body-measurements/table'
import { BodyMeasurementGoals } from '@src/routes/members/components/body-measurements/body-measurements-goals'
import { bulletColorMapping, lineColorMapping } from '@src/util/constants'
import { MetricData } from '@src/data/body-measurement'
import { Chart } from '@src/components/chart/chart'
import { Legend } from '@src/components/legend/legend'
import { Button } from '@src/elements/button/button'
import { BodyMeasurementsCard } from '@src/routes/members/components/body-measurements/body-measurements-card'
import { BodyMeasurementsService } from '@src/service/body-measurements'

import './circumferences.sass'
import { LengthUnit, WeightUnit } from '@src/util/weight'

@Component({
  computed: {
    lengthUnit () {
      return this.$store.state.club.weightUnit === WeightUnit.KG ? LengthUnit.CM : LengthUnit.IN
    }
  }
})
export class Circumferences extends Vue {

  protected static MEASUREMENT_TYPES = ['waistline', 'right_bicep_circumference', 'left_bicep_circumference', 'right_thigh_circumference', 'left_thigh_circumference',
    'hip_measurement', 'chest_circumference']

  @Prop({ required: true })
  protected measurements: {
    waistline: MetricData[],
    leftBicepCircumference: MetricData[],
    rightBicepCircumference: MetricData[],
    leftThighCircumference: MetricData[],
    rightThighCircumference: MetricData[],
    hipMeasurement: MetricData[],
    chestCircumference: MetricData[]
  }

  @Prop({ required: true })
  protected bodyCircumferenceNotes: string

  @Prop({ required: true })
  protected goals: any

  private selectedTab: 'measurements' | 'goals' = 'measurements'
  private isCardExpanded: boolean = false

  private selectedFilters: string[] = []
  private lengthUnit: LengthUnit

  constructor () {
    super()
    this.selectedFilters = Object.assign([], Circumferences.MEASUREMENT_TYPES) || /* istanbul ignore next: Object.assign always returns the array */ []
  }

  expandCard () {
    this.isCardExpanded = !this.isCardExpanded
  }

  selectTab (tabName: 'measurements' | 'goals') {
    this.selectedTab = tabName
  }

  filter (measurementType) {
    let index = this.selectedFilters.indexOf(measurementType)
    if (index >= 0) {
      this.selectedFilters.splice(index, 1)
    } else {
      this.selectedFilters.push(measurementType)
    }
  }

  render (h: CreateElement) {
    return <div class='measurements-section circumferences-section'>
      <Subtitle1 class='title'>{this.$t('BODY_MEASUREMENTS.CIRCUMFERENCES')}</Subtitle1>

      <div class='device-measurement-section-wrapper'>
        <Legend select={this.filter} items={
          Circumferences.MEASUREMENT_TYPES.map(item => {
            return {
              type: item,
              label: this.$t(`BODY_MEASUREMENTS.CIRCUMFERENCE.${item.toUpperCase()}`),
              color: bulletColorMapping[item],
              disabled: !this.selectedFilters.includes(item)
            }
          })
        } />

        <Chart chartId='circumferencesChart' series={this.getChartSeries()}
               tooltipPointFormat={'<div>{point.y}' + this.lengthUnit + '</div>'}
               emptyGraphMessage={this.$t('BODY_MEASUREMENTS.EMPTY_GRAPH.CIRCUMFERENCES')} />

        <div class='cards-header-row row'>
          {this.isCardExpanded ? <div class={'cards-header-legend'}><Legend select={this.selectTab} items={[
            { type: 'measurements', label: this.$t('BODY_MEASUREMENTS.DATA_INPUT_OVERVIEW'), disabled: this.selectedTab !== 'measurements' },
            { type: 'goals', label: this.$t('BODY_MEASUREMENTS.GOALS'), disabled: this.selectedTab !== 'goals' }
          ]} /></div> : null}

          <Button type='remove' click={this.expandCard} icon={this.isCardExpanded ? 'util/caret-up' : 'util/caret-down'}
                  label={this.$t(this.isCardExpanded ? 'COLLAPSE' : 'EXPAND')} ref='expandBtn' class='expand-btn' />
        </div>

        {this.isCardExpanded ?
          this.selectedTab === 'measurements' ?
            this.renderCard(h)

            : this.renderGoalsCard(h)

          : null}
      </div>
    </div>
  }

  renderCard (h: CreateElement) {
    return <BodyMeasurementsCard type='circumferences'>
      <div slot='left'>
        <CircumferencesForm notes={{ bodyCircumferenceNotes: this.bodyCircumferenceNotes }} />
      </div>
      <div slot='right'>
        <HistoryTable history={this.getTableHistory()} measuringUnit={this.lengthUnit.toString()} title={this.$t('BODY_MEASUREMENTS.CIRCUMFERENCE.KNOWN_DATA')}
                      legendItems={Circumferences.MEASUREMENT_TYPES} translationKey='BODY_MEASUREMENTS.CIRCUMFERENCE.' type='circumferences' />
      </div>
    </BodyMeasurementsCard>
  }

  renderGoalsCard (h: CreateElement) {
    let formData = {
      waistline: null,
      right_bicep_circumference: null,
      left_bicep_circumference: null,
      right_thigh_circumference: null,
      left_thigh_circumference: null,
      hip_measurement: null,
      chest_circumference: null
    }
    Object.keys(formData).map(key => (!this.goals[key]) ? this.goals[key] = [] : null)
    return <BodyMeasurementsCard type='circumferences-goals'>
      <BodyMeasurementGoals formData={formData} goals={this.goals} translationKey='BODY_MEASUREMENTS.CIRCUMFERENCE.' type='circumferences-goals' />
    </BodyMeasurementsCard>
  }

  protected getChartSeries () {
    return [
      {
        data: this.selectedFilters.includes('waistline') ? BodyMeasurementsService.convertToChartDTO(this.measurements.waistline) : [],
        name: this.$t('BODY_MEASUREMENTS.CIRCUMFERENCE.WAISTLINE'),
        color: lineColorMapping['waistline']
      }, {
        data: this.selectedFilters.includes('right_bicep_circumference') ? BodyMeasurementsService.convertToChartDTO(this.measurements.rightBicepCircumference) : [],
        name: this.$t('BODY_MEASUREMENTS.CIRCUMFERENCE.RIGHT_BICEP_CIRCUMFERENCE'),
        color: lineColorMapping['right_bicep_circumference']
      }, {
        data: this.selectedFilters.includes('left_bicep_circumference') ? BodyMeasurementsService.convertToChartDTO(this.measurements.leftBicepCircumference) : [],
        name: this.$t('BODY_MEASUREMENTS.CIRCUMFERENCE.LEFT_BICEP_CIRCUMFERENCE'),
        color: lineColorMapping['left_bicep_circumference']
      }, {
        data: this.selectedFilters.includes('right_thigh_circumference') ? BodyMeasurementsService.convertToChartDTO(this.measurements.rightThighCircumference) : [],
        name: this.$t('BODY_MEASUREMENTS.CIRCUMFERENCE.RIGHT_THIGH_CIRCUMFERENCE'),
        color: lineColorMapping['right_thigh_circumference']
      }, {
        data: this.selectedFilters.includes('left_thigh_circumference') ? BodyMeasurementsService.convertToChartDTO(this.measurements.leftThighCircumference) : [],
        name: this.$t('BODY_MEASUREMENTS.CIRCUMFERENCE.LEFT_THIGH_CIRCUMFERENCE'),
        color: lineColorMapping['left_thigh_circumference']
      }, {
        data: this.selectedFilters.includes('hip_measurement') ? BodyMeasurementsService.convertToChartDTO(this.measurements.hipMeasurement) : [],
        name: this.$t('BODY_MEASUREMENTS.CIRCUMFERENCE.HIP_MEASUREMENT'),
        color: lineColorMapping['hip_measurement']
      }, {
        data: this.selectedFilters.includes('chest_circumference') ? BodyMeasurementsService.convertToChartDTO(this.measurements.chestCircumference) : [],
        name: this.$t('BODY_MEASUREMENTS.CIRCUMFERENCE.CHEST_CIRCUMFERENCE'),
        color: lineColorMapping['chest_circumference']
      }
    ]
  }

  protected getTableHistory () {
    return [...BodyMeasurementsService.convertToHistoryDTO(this.measurements.waistline, 'waistline'),
      ...BodyMeasurementsService.convertToHistoryDTO(this.measurements.rightBicepCircumference, 'right_bicep_circumference'),
      ...BodyMeasurementsService.convertToHistoryDTO(this.measurements.leftBicepCircumference, 'left_bicep_circumference'),
      ...BodyMeasurementsService.convertToHistoryDTO(this.measurements.rightThighCircumference, 'right_thigh_circumference'),
      ...BodyMeasurementsService.convertToHistoryDTO(this.measurements.leftThighCircumference, 'left_thigh_circumference'),
      ...BodyMeasurementsService.convertToHistoryDTO(this.measurements.hipMeasurement, 'hip_measurement'),
      ...BodyMeasurementsService.convertToHistoryDTO(this.measurements.chestCircumference, 'chest_circumference')]
      .slice().sort((a, b) => a.effectiveAt > b.effectiveAt ? -1 : a.effectiveAt < b.effectiveAt ? 1 : 0)
  }
}
